import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
// import { LoadingButton } from '@mui/lab';
import { Box, Typography, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { API_URL } from '../../../../links/API_LINKS';
import SuperCapstoneForm from './SuperCapstoneForm';

const CreateNewSuperCapstone = ({
  setCapStoneEditData,
  capstoneData,
  setActiveStep,
  setSuperCapstoneId,
  superCapstoneId,
}) => {
  const formikRef = useRef();
  const [serverResponse, setServerResponse] = useState();

  // Create a more robust validation schema
  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    domainId: Yup.string().required('Domain is required'),
    companyId: Yup.string().required('Company is required'),
    level: Yup.string().required('Level is required'),
    // skillId: Yup.array().min(1, 'Minimum 1 skill required'),
    problemStatement: Yup.string().required('Problem Statement is required'),
    projectOverview: Yup.string().nullable().default(''),
    skillDescription: Yup.string().nullable().default(''),
    nextSteps: Yup.string().nullable().default(''),
    learningObjectives: Yup.string().required(
      'Learning Objectives is required'
    ),
    businessObjectives: Yup.string().required(
      'Business Objectives is required'
    ),
    prerequisites: Yup.string(),
    tools: Yup.string(),
    dataset: Yup.object().shape({
      link: Yup.string().url('Dataset link must be a valid URL'),
      description: Yup.string().when('link', {
        is: (link) => link && link.trim() !== '',
        then: Yup.string().required(
          'Dataset description is required when dataset link is provided'
        ),
        otherwise: Yup.string(),
      }),
    }),
  });

  // Ensure initial values have these fields defined
  const getInitialValues = () => {
    console.log('Getting initial values, capstoneData:', capstoneData);

    // Make sure initial values include all required fields
    const initialValues = {
      ...capstoneData,
      title: capstoneData?.title || '',
      domainId: capstoneData?.domainId || '',
      companyId: capstoneData?.companyId || '',
      level: capstoneData?.level || 'Basic',
      // skillId: capstoneData?.skillId || [],
      problemStatement: capstoneData?.problemStatement || '',
      projectOverview: capstoneData?.projectOverview || '',
      skillDescription: capstoneData?.skillDescription || '',
      nextSteps: capstoneData?.nextSteps || '',
      learningObjectives: capstoneData?.learningObjectives || '',
      businessObjectives: capstoneData?.businessObjectives || '',
      prerequisites: capstoneData?.prerequisites || '',
      tools: capstoneData?.tools || '',
      dataset: capstoneData?.dataset || {
        link: '',
        description: '',
      },
    };

    console.log('Initial form values:', initialValues);
    return initialValues;
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setServerResponse('');

    // Log the form values as they are about to be submitted
    console.log('===== FORM SUBMISSION STARTED =====');
    console.log('All Form Values:', values);
    console.log('Project Overview type:', typeof values.projectOverview);
    console.log('Project Overview raw:', values.projectOverview);
    console.log('Skill Description type:', typeof values.skillDescription);
    console.log('Skill Description raw:', values.skillDescription);

    // Helper function to safely handle HTML content
    const prepareHtmlField = (htmlContent) => {
      // If null or undefined, return empty string
      if (htmlContent === null || htmlContent === undefined) {
        return '';
      }

      // If it's already a string, return it
      if (typeof htmlContent === 'string') {
        return htmlContent;
      }

      // Otherwise, convert to string (safeguard)
      return String(htmlContent);
    };

    // Create clean data object to send
    const dataToSend = {
      ...values,
      // Ensure we're sending proper string values for HTML fields
      projectOverview: prepareHtmlField(values.projectOverview),
      skillDescription: prepareHtmlField(values.skillDescription),
      nextSteps: prepareHtmlField(values.nextSteps),
    };

    // Add superCapstoneId if needed
    if (superCapstoneId) {
      dataToSend.superCapstoneId = superCapstoneId;
    }

    // FINAL CHECK: Verify data integrity before sending
    console.log('===== FINAL DATA CHECK BEFORE SENDING =====');
    console.log('Project Overview in payload:', dataToSend.projectOverview);
    console.log('Skill Description in payload:', dataToSend.skillDescription);
    console.log('Next Steps in payload:', dataToSend.nextSteps);
    console.log('Final data payload:', JSON.stringify(dataToSend, null, 2));

    const sendMethod = superCapstoneId ? 'PUT' : 'POST';

    try {
      // Make the API request with appropriate headers
      console.log('Making API request to:', API_URL + '/super-capstone/');

      const response = await axios({
        method: sendMethod,
        url: API_URL + '/super-capstone/',
        data: dataToSend,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Log successful response
      console.log('===== SERVER RESPONSE (SUCCESS) =====');
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      // Verify the fields were saved correctly
      if (response.data && response.data.supercapstone) {
        console.log('===== FIELD SAVE VERIFICATION =====');
        console.log(
          'Project Overview saved:',
          response.data.supercapstone.projectOverview ? 'YES' : 'NO'
        );
        console.log(
          'Skill Description saved:',
          response.data.supercapstone.skillDescription ? 'YES' : 'NO'
        );
        console.log(
          'Project Overview value from server:',
          response.data.supercapstone.projectOverview
        );
        console.log(
          'Skill Description value from server:',
          response.data.supercapstone.skillDescription
        );
      }

      // Update state with successful response data
      setServerResponse('Capstone added successfully!');
      setSuperCapstoneId(response.data.supercapstone._id);
      setCapStoneEditData(response.data.supercapstone);
      setActiveStep(1);
    } catch (error) {
      // Log error details
      console.log('===== SERVER RESPONSE (ERROR) =====');
      console.log('Error:', error);

      if (error.response) {
        console.log('Status:', error.response.status);
        console.log('Data:', error.response.data);
        setServerResponse(
          `Error: ${error.response.data?.message || 'Server error'}`
        );
      } else if (error.request) {
        console.log('No response received');
        setServerResponse('Network error: No response from server');
      } else {
        console.log('Request error:', error.message);
        setServerResponse(`Request error: ${error.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className=''>
      <Box>
        <Typography variant='h6'>Add a new capstone</Typography> <br />
      </Box>
      <div>
        <Formik
          innerRef={formikRef}
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}>
          {({ values, setFieldValue, isSubmitting, errors, touched }) => (
            <Form>
              {/* Debug section - remove in production */}
              <div style={{ display: 'none' }}>
                <pre>
                  {JSON.stringify(
                    {
                      projectOverview: values.projectOverview,
                      skillDescription: values.skillDescription,
                    },
                    null,
                    2
                  )}
                </pre>
              </div>

              <SuperCapstoneForm
                capstoneData={capstoneData}
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 1,
                  m: 1,
                }}>
                {serverResponse && (
                  <Typography
                    color={
                      serverResponse.includes('Error') ? 'error' : 'success'
                    }>
                    {serverResponse}
                  </Typography>
                )}
              </Box>

              <Stack
                direction='row'
                justifyContent='center'
                alignItems='center'
                spacing={3}
                mt={2}>
                <LoadingButton
                  // type='submit' /* Commented out to prevent double submission */
                  variant='contained'
                  loading={isSubmitting}
                  onClick={() => {
                    // Extra logging on button click
                    console.log('===== SUBMIT BUTTON CLICKED =====');
                    console.log('Current form values:');
                    console.log('- projectOverview:', values.projectOverview);
                    console.log('- skillDescription:', values.skillDescription);
                    console.log('- nextSteps:', values.nextSteps);

                    // Force Formik to use current values by manually triggering submit
                    // This bypasses any potential race conditions
                    const currentValues = { ...values };
                    setTimeout(() => {
                      handleSubmit(currentValues, {
                        setSubmitting: (isSubmitting) =>
                          console.log('setSubmitting:', isSubmitting),
                      });
                    }, 100);
                  }}>
                  Save
                </LoadingButton>
              </Stack>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CreateNewSuperCapstone;
